import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const STORAGE_MSG_KEY = "farmer_npc_msg_index";

function makeIdemKey() {
  const r =
    window.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random()
      .toString(16)
      .slice(2)}`;
  return String(r).slice(0, 120).padEnd(12, "0");
}

function makeReqId() {
  return (
    window.crypto?.randomUUID?.() ||
    `req-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

const NPC_REWARD_TEXT = {
  Energy: "⚡ Energy",
  Water: "💧 Water",
  Wheat: "🌾 Wheat",
  Corn: "🌽 Corn",
  Milk: "🥛 Milk",
  "Wheat Bag": "🎒 Wheat Bag",
  "Corn Bag": "🎒 Corn Bag",
  Egg: "🥚 Egg",
  Carbon: "🧱 Carbon",
  Wood: "🪵 Wood",
};

function safeItemLabel(item) {
  if (typeof item === "string") return NPC_REWARD_TEXT[item] || item;
  if (item && typeof item === "object") {
    const key = item.item || item.name || item.id || "Reward";
    return NPC_REWARD_TEXT[key] || key;
  }
  return "Reward";
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}
function rand(a, b) {
  return a + Math.random() * (b - a);
}

export function FarmerNPC({
  apiClaimUrl = "/api/npcFarmer/claim",
  apiTokenUrl = "/api/npcFarmer/token",
  enabled = true,

  // Tamaño + calidad visual
  size = 72, // un poquito más grande por defecto
  sprite = "https://res.cloudinary.com/dpwv380jp/image/upload/v1765771143/nova_od1mii.png",
  spriteRender = "auto", // "auto" | "crisp-edges" | "pixelated"
  hiDpiBoost = true, // mejora nitidez en pantallas retina

  // Movimiento
  bounds = { left: 6, right: 94, top: 18, bottom: 90 },
  stepMs = 120,
  speed = 0.55,
  newTargetEveryMs = 2200,
  idleChance = 0.18,
  idleMsRange = [450, 1200],

  // Mensajes: “que duren más” + sorpresa
  fxDurations = {
    win: 5200, // premio
    info: 4200, // tips/estado
    miss: 4200, // cooldown/errores
    daily: 5200, // “recompensas hoy”
  },
  tipCountAfterActions = 2,
  tipGapMs = 1700,

  // Aura / glow
  aura = {
    enabled: true,
    // intensidad general (sube para más brillo)
    strength: 1.0,
    // tamaño relativo del aura
    scale: 1.08,
    // muestra partículas suaves orbitando
    particles: true,
  },

  onReward,
  onMessage,
  onUnauthorized,
}) {
  const npcRef = useRef(null);

  const [pos, setPos] = useState({ x: 40, y: 56 });
  const [target, setTarget] = useState({ x: 80, y: 56 });
  const [dir, setDir] = useState(1);

  const [busy, setBusy] = useState(false);
  const [idleUntil, setIdleUntil] = useState(0);

  const [npcToken, setNpcToken] = useState(null);

  // FX system (cola de mensajes, para que duren y no se pisen)
  const [fx, setFx] = useState(null);
  const fxTimerRef = useRef(null);
  const fxQueueRef = useRef([]);
  const tipTimersRef = useRef([]);

  const inFlightRef = useRef(false);

  const tips = useMemo(
    () => [
      "👋 Welcome farmer!",
      "💧 Buy your water well to get Water",
      "🌱 Buy seeds to start planting",
      "✅ Collect your lands when they are READY",
      "⚡ The mill produces Energy",
      "🛒 Visit the Market to trade items",
      "🐄 Cows produce Milk over time",
      "🐔 Chickens give Eggs",
      "📦 Seeds are stored in your inventory",
      "🔥 Tip: Upgrade your lands to earn faster!",
    ],
    []
  );

  const nextTip = useCallback(() => {
    const idx = Number(window.localStorage.getItem(STORAGE_MSG_KEY) || 0);
    const msg = tips[idx % tips.length];
    window.localStorage.setItem(STORAGE_MSG_KEY, String(idx + 1));
    return msg;
  }, [tips]);

  const clearTipTimers = useCallback(() => {
    tipTimersRef.current.forEach((t) => clearTimeout(t));
    tipTimersRef.current = [];
  }, []);

  const clearFxTimer = useCallback(() => {
    if (fxTimerRef.current) clearTimeout(fxTimerRef.current);
    fxTimerRef.current = null;
  }, []);

  const flushFxQueue = useCallback(() => {
    clearFxTimer();
    setFx(null);
    fxQueueRef.current = [];
  }, [clearFxTimer]);

  const showFxNow = useCallback(
    (kind, text, ms) => {
      clearFxTimer();
      setFx({ kind, text });
      onMessage?.(text);

      if (ms > 0) {
        fxTimerRef.current = setTimeout(() => {
          setFx(null);
          fxTimerRef.current = null;

          // si hay cola, mostramos el siguiente
          const next = fxQueueRef.current.shift();
          if (next) showFxNow(next.kind, next.text, next.ms);
        }, ms);
      }
    },
    [clearFxTimer, onMessage]
  );

  const enqueueFx = useCallback(
    (kind, text, ms) => {
      // si no hay fx activo, lo mostramos ya; si hay, lo encolamos
      if (!fx) showFxNow(kind, text, ms);
      else fxQueueRef.current.push({ kind, text, ms });
    },
    [fx, showFxNow]
  );

  const chainTips = useCallback(
    (count = tipCountAfterActions, gapMs = tipGapMs) => {
      clearTipTimers();
      for (let i = 0; i < count; i++) {
        const t = setTimeout(() => {
          enqueueFx("info", nextTip(), fxDurations.info);
        }, (i + 1) * gapMs);
        tipTimersRef.current.push(t);
      }
    },
    [
      clearTipTimers,
      enqueueFx,
      nextTip,
      fxDurations.info,
      tipCountAfterActions,
      tipGapMs,
    ]
  );

  useEffect(() => {
    return () => {
      clearFxTimer();
      clearTipTimers();
    };
  }, [clearFxTimer, clearTipTimers]);

  const pickNewTarget = useCallback(() => {
    const x = rand(bounds.left, bounds.right);
    const y = rand(bounds.top, bounds.bottom);
    setTarget({ x, y });
  }, [bounds.left, bounds.right, bounds.top, bounds.bottom]);

  useEffect(() => {
    pickNewTarget();
    const t = setInterval(() => pickNewTarget(), newTargetEveryMs);
    return () => clearInterval(t);
  }, [pickNewTarget, newTargetEveryMs]);

  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      if (!enabled) return;
      if (now < idleUntil) return;

      setPos((p) => {
        const dx = target.x - p.x;
        const dy = target.y - p.y;

        if (Math.abs(dx) < 0.6 && Math.abs(dy) < 0.6) {
          if (Math.random() < idleChance) {
            const [a, b] = idleMsRange;
            setIdleUntil(Date.now() + rand(a, b));
          }
          return p;
        }

        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const vx = (dx / dist) * speed;
        const vy = (dy / dist) * speed;

        if (vx > 0.02) setDir(1);
        if (vx < -0.02) setDir(-1);

        return {
          x: clamp(p.x + vx, bounds.left, bounds.right),
          y: clamp(p.y + vy, bounds.top, bounds.bottom),
        };
      });
    }, stepMs);

    return () => clearInterval(t);
  }, [
    enabled,
    target.x,
    target.y,
    bounds.left,
    bounds.right,
    bounds.top,
    bounds.bottom,
    stepMs,
    speed,
    idleUntil,
    idleChance,
    idleMsRange,
  ]);

  async function ensureNpcToken(force = false) {
    if (!force && npcToken) return npcToken;

    const reqId = makeReqId();

    try {
      const { data } = await axios.get(apiTokenUrl, {
        withCredentials: true,
        headers: { "x-req-id": reqId },
      });

      if (!data?.npcAccessToken) throw new Error("No npcAccessToken");

      setNpcToken(data.npcAccessToken);
      return data.npcAccessToken;
    } catch (err) {
      console.log("NPC TOKEN ERROR:", {
        url: err?.config?.url,
        status: err?.response?.status,
        data: err?.response?.data,
        reqId,
      });

      const status = err?.response?.status;
      if (status === 401) {
        const e = new Error("Unauthorized");
        e.code = 401;
        throw e;
      }
      throw new Error(err?.response?.data?.error || err?.message || "Error");
    }
  }

  async function claimRewardFromServer() {
    const idempotencyKey = makeIdemKey();
    const reqId = makeReqId();

    async function doClaim(token) {
      const { data } = await axios.post(
        apiClaimUrl,
        { idempotencyKey },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "x-req-id": reqId,
          },
        }
      );
      return data;
    }

    try {
      const token1 = await ensureNpcToken(false);
      return await doClaim(token1);
    } catch (err) {
      const status = err?.response?.status;

      // retry 1 vez si 401 (token NPC vencido)
      if (status === 401) {
        setNpcToken(null);
        const token2 = await ensureNpcToken(true);
        return await doClaim(token2);
      }

      throw new Error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Server error"
      );
    }
  }

  const handleClick = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    e?.nativeEvent?.stopImmediatePropagation?.();

    if (!enabled) return;
    if (busy) return;
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    setBusy(true);
    clearTipTimers();
    flushFxQueue();
    showFxNow("info", "⏳ Claiming reward...", 0);

    try {
      const data = await claimRewardFromServer();

      if (data?.allowed === false) {
        const rem = Number(data?.remainingMs || 0);
        const h = Math.floor(rem / 3600000);
        const m = Math.floor((rem % 3600000) / 60000);

        showFxNow("miss", `⏳ Come back in ${h}h ${m}m`, fxDurations.miss);
        chainTips(1, tipGapMs);
        return;
      }

      if (data?.won && data?.reward) {
        const label = safeItemLabel(data.reward.item);
        const qty = Number(data.reward.qty ?? 1);

        // 🎉 premio “más cinematográfico”
        showFxNow("win", `🎁 You received ${label} x${qty}!`, fxDurations.win);
        onReward?.(data.reward);

        // tip(s) y estado después
        chainTips(tipCountAfterActions, tipGapMs);
      } else {
        // No ganó, igual damos feedback bonito
        showFxNow("info", "✨ Not this time — keep trying!", fxDurations.info);
        chainTips(1, tipGapMs);
      }

      if (typeof data?.todaysCount === "number") {
        // lo encolamos para que no pise el premio
        const dailyMax = data?.dailyMaxRewards ?? "?";
        enqueueFx(
          "info",
          `📅 Daily rewards: ${data.todaysCount}/${dailyMax}`,
          fxDurations.daily
        );
      }
    } catch (err) {
      console.log("NPC HANDLECLICK CATCH:", {
        code: err?.code,
        message: err?.message,
      });

      if (err?.code === 401) {
        showFxNow("miss", "🔒 Unauthorized. Please log in again.", fxDurations.miss);
        onUnauthorized?.(err);
      } else {
        showFxNow("miss", `⚠️ ${err?.message || "Error"}`, fxDurations.miss);
      }
      chainTips(1, 2200);
    } finally {
      setBusy(false);
      inFlightRef.current = false;
    }
  };

  // --- Visual helpers ---
  const strength = aura?.strength ?? 1.0;
  const auraOn = !!aura?.enabled;

  // halo sizes based on NPC size
  const haloSize = Math.round(size * 1.35);
  const ringSize = Math.round(size * 1.55);

  const imgStyle = {
    width: "100%",
    transform: `scaleX(${dir})`,
    imageRendering: spriteRender,
    WebkitFontSmoothing: "antialiased",
    // “nitidez” suave + sombra/realce
    filter: busy
      ? `drop-shadow(0 10px 18px rgba(0,0,0,0.55)) brightness(${1.08 + 0.05 * strength}) contrast(${1.08 + 0.05 * strength})`
      : `drop-shadow(0 7px 12px rgba(0,0,0,0.48)) brightness(${1.03 + 0.04 * strength}) contrast(${1.06 + 0.04 * strength})`,
  };

  // fx color
  const fxColor =
    fx?.kind === "win" ? "#7CFF9C" : fx?.kind === "info" ? "#7CCBFF" : "#FFD27C";

  return (
    <Box
      ref={npcRef}
      onClick={handleClick}
      sx={{
        position: "absolute",
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        width: size,
        zIndex: 40,
        cursor: enabled ? "pointer" : "not-allowed",
        userSelect: "none",
        transform: "translate(-50%, -50%)",
        // mejora en pantallas retina (pequeño truco de escalado)
        ...(hiDpiBoost
          ? {
              transformOrigin: "center",
              willChange: "transform",
            }
          : null),
      }}
    >
      {/* AURA / HALO */}
      {auraOn && (
        <>
          {/* halo suave animado */}
          <motion.div
            aria-hidden
            style={{
              position: "absolute",
              left: "50%",
              top: "55%",
              width: haloSize,
              height: haloSize,
              transform: "translate(-50%, -50%)",
              borderRadius: "50%",
              pointerEvents: "none",
              background:
                "radial-gradient(circle, rgba(124,203,255,0.35) 0%, rgba(124,255,156,0.18) 35%, rgba(255,210,124,0.10) 58%, rgba(0,0,0,0) 72%)",
              filter: `blur(${Math.max(10, Math.round(size * 0.18))}px)`,
              opacity: busy ? 0.9 : 0.75,
              mixBlendMode: "screen",
            }}
            animate={{
              scale: [1.0, aura?.scale ?? 1.08, 1.0],
              opacity: busy ? [0.85, 0.98, 0.85] : [0.65, 0.82, 0.65],
              rotate: [0, 12, 0],
            }}
            transition={{
              duration: busy ? 1.2 : 1.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* anillo brillante */}
          <motion.div
            aria-hidden
            style={{
              position: "absolute",
              left: "50%",
              top: "58%",
              width: ringSize,
              height: ringSize,
              transform: "translate(-50%, -50%)",
              borderRadius: "50%",
              pointerEvents: "none",
              background:
                "conic-gradient(from 180deg, rgba(124,203,255,0.0), rgba(124,203,255,0.28), rgba(124,255,156,0.22), rgba(255,210,124,0.18), rgba(124,203,255,0.0))",
              maskImage:
                "radial-gradient(circle, transparent 56%, black 57%, black 64%, transparent 66%)",
              WebkitMaskImage:
                "radial-gradient(circle, transparent 56%, black 57%, black 64%, transparent 66%)",
              filter: `blur(${Math.max(2, Math.round(size * 0.04))}px)`,
              opacity: 0.75 + 0.12 * strength,
              mixBlendMode: "screen",
            }}
            animate={{
              rotate: [0, 360],
              scale: [1.0, 1.04, 1.0],
            }}
            transition={{
              rotate: { duration: 6.5, repeat: Infinity, ease: "linear" },
              scale: { duration: 1.7, repeat: Infinity, ease: "easeInOut" },
            }}
          />

          {/* partículas sutiles */}
          {aura?.particles && (
            <motion.div
              aria-hidden
              style={{
                position: "absolute",
                left: "50%",
                top: "58%",
                width: ringSize + 12,
                height: ringSize + 12,
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
                borderRadius: "50%",
                overflow: "visible",
                mixBlendMode: "screen",
                opacity: 0.65,
              }}
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 10.5, repeat: Infinity, ease: "linear" }}
            >
              {Array.from({ length: 7 }).map((_, i) => {
                const a = (i / 7) * Math.PI * 2;
                const r = ringSize * 0.48;
                const x = Math.cos(a) * r;
                const y = Math.sin(a) * r;
                const s = 6 + (i % 3) * 2;
                return (
                  <motion.div
                    key={i}
                    style={{
                      position: "absolute",
                      left: `calc(50% + ${x}px)`,
                      top: `calc(50% + ${y}px)`,
                      width: s,
                      height: s,
                      borderRadius: "50%",
                      background:
                        "radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(124,203,255,0.55) 35%, rgba(0,0,0,0) 72%)",
                      filter: "blur(0.2px)",
                      opacity: 0.75,
                    }}
                    animate={{
                      y: [0, -3, 0],
                      opacity: [0.55, 0.95, 0.55],
                      scale: [1, 1.25, 1],
                    }}
                    transition={{
                      duration: 1.2 + (i % 4) * 0.25,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.12,
                    }}
                  />
                );
              })}
            </motion.div>
          )}
        </>
      )}

      {/* NPC IMG */}
      <motion.img
        src={sprite}
        alt="Farmer"
        draggable={false}
        style={imgStyle}
        animate={{ y: [0, -2.5, 0] }}
        transition={{ duration: 0.9, repeat: Infinity }}
      />

      {/* Mensajes */}
      <AnimatePresence>
        {fx && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: -20, scale: 1 }}
            exit={{ opacity: 0, y: -34, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            style={{
              position: "absolute",
              top: -18,
              left: "50%",
              transform: "translateX(-50%)",
              pointerEvents: "none",
              width: "min(320px, 80vw)",
              maxWidth: 320,
            }}
          >
            <Box
              sx={{
                width: "100%",
                px: 1.25,
                py: 0.8,
                borderRadius: 2.2,
                background: "rgba(0,0,0,0.76)",
                border: "1px solid rgba(255,255,255,0.14)",
                boxShadow: "0 12px 26px rgba(0,0,0,0.45)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* brillo animado detrás del texto (queda brutal) */}
              <motion.div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: -30,
                  background:
                    "radial-gradient(circle at 30% 20%, rgba(124,203,255,0.24), rgba(0,0,0,0) 45%), radial-gradient(circle at 70% 70%, rgba(124,255,156,0.18), rgba(0,0,0,0) 52%)",
                  opacity: 0.85,
                }}
                animate={{
                  x: [0, 10, 0],
                  y: [0, -8, 0],
                }}
                transition={{
                  duration: 2.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <Typography
                sx={{
                  position: "relative",
                  fontSize: 13,
                  fontWeight: 950,
                  lineHeight: 1.15,
                  color: fxColor,
                  textShadow: "0 2px 0 rgba(0,0,0,0.85)",
                  overflowWrap: "anywhere",
                  whiteSpace: "normal",
                  letterSpacing: 0.2,
                }}
              >
                {fx.text}
              </Typography>
            </Box>

            <Box
              sx={{
                width: 0,
                height: 0,
                mx: "auto",
                borderLeft: "9px solid transparent",
                borderRight: "9px solid transparent",
                borderTop: "11px solid rgba(0,0,0,0.76)",
                filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.4))",
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* “click pulse” */}
      <AnimatePresence>
        {busy && (
          <motion.div
            aria-hidden
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1.35 }}
            exit={{ opacity: 0, scale: 1.8 }}
            transition={{ duration: 0.35 }}
            style={{
              position: "absolute",
              left: "50%",
              top: "60%",
              transform: "translate(-50%, -50%)",
              width: ringSize * 0.75,
              height: ringSize * 0.75,
              borderRadius: "50%",
              border: "1px solid rgba(124,203,255,0.35)",
              boxShadow:
                "0 0 18px rgba(124,203,255,0.22), 0 0 28px rgba(124,255,156,0.14)",
              pointerEvents: "none",
              mixBlendMode: "screen",
            }}
          />
        )}
      </AnimatePresence>
    </Box>
  );
}
